import React, { useMemo, useState } from "react";
import { FiMessageSquare, FiPhone, FiVideo } from "react-icons/fi";
import moment from "moment";
import clsx from "clsx";
import { useUserGetMyCallsQuery } from "../../../../redux/rtk-api"; // adjust path as needed

interface Call {
  id: number;
  name: string;
  callType: "chat" | "audio" | "video";
  startTime: string;
  endTime: string;
  duration: string;
  status: "Completed" | "Ongoing" | "Upcoming";
  roomId?: string;
}

const ITEMS_PER_PAGE = 10;

const CallHistoryTable: React.FC = () => {
  const { data, isLoading, isError } = useUserGetMyCallsQuery();
  const [currentPage, setCurrentPage] = useState(1);

  const calls: Call[] = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((item, idx) => {
      const now = new Date();
      const start = new Date(item.sessionDetails?.startTime);
      const end = new Date(item.sessionDetails?.endTime);

      const status =
        now >= start && now <= end
          ? "Ongoing"
          : now > end
          ? "Completed"
          : "Upcoming";

      return {
        id: idx + 1,
        name: `${item.users?.user?.name?.firstName ?? ""} ${item.users?.user?.name?.lastName ?? ""}`,
        callType: item.sessionDetails?.callType ?? "chat",
        startTime: item.sessionDetails?.startTime,
        endTime: item.sessionDetails?.endTime,
        duration: item.sessionDetails?.duration ?? "N/A",
        status,
        roomId: item.sessionDetails?.roomId,
      };
    });
  }, [data]);

  const totalPages = Math.ceil(calls.length / ITEMS_PER_PAGE);
  const paginatedData = calls.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const renderIcon = (type: string) => {
    if (type === "chat") return <FiMessageSquare size={18} />;
    if (type === "audio") return <FiPhone size={18} />;
    if (type === "video") return <FiVideo size={18} />;
    return null;
  };

  if (isLoading) return <p className="p-4 text-center">Loading...</p>;
  if (isError)
    return <p className="p-4 text-center text-red-500">Failed to fetch data.</p>;
  if (!isLoading && calls.length === 0)
    return <p className="p-4 text-center text-gray-500">No call history found.</p>;

  return (
    <div className="bg-pink-50 rounded-xl p-4">
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border rounded-lg">
          <thead>
            <tr className="bg-pink-200 text-left text-sm font-semibold text-gray-700">
              <th className="px-4 py-2">SR</th>
              <th className="px-4 py-2">USER NAME</th>
              <th className="px-4 py-2">CALL TYPE</th>
              <th className="px-4 py-2">START TIME</th>
              <th className="px-4 py-2">END TIME</th>
              <th className="px-4 py-2">DURATION</th>
              <th className="px-4 py-2">STATUS</th>
              <th className="px-4 py-2">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((call, idx) => (
              <tr key={call.id} className="text-sm hover:bg-gray-50 border-t">
                <td className="px-4 py-2">
                  {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                </td>
                <td className="px-4 py-2 capitalize">{call.name}</td>
                <td className="px-4 py-2 flex items-center gap-2 capitalize">
                  {renderIcon(call.callType)} {call.callType}
                </td>
                <td className="px-4 py-2">
                  {moment(call.startTime).format("ll LT")}
                </td>
                <td className="px-4 py-2">
                  {moment(call.endTime).format("ll LT")}
                </td>
                <td className="px-4 py-2">{call.duration}</td>
                <td className="px-4 py-2 font-medium capitalize">
                  {call.status}
                </td>
                <td className="px-4 py-2">
                  {call.status === "Ongoing" && call.roomId ? (
                    <a
                      href={`https://alter-videoconf-1123.app.100ms.live/meeting/${call.roomId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline font-medium"
                    >
                      Join
                    </a>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
          className={clsx(
            "px-4 py-1 rounded",
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-primary-500 text-white hover:bg-primary-600"
          )}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
          className={clsx(
            "px-4 py-1 rounded",
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-primary-500 text-white hover:bg-primary-600"
          )}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CallHistoryTable;
